import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Wallet as WalletIcon, Plus, ArrowDownLeft, ArrowUpRight,
  CreditCard, TrendingUp, History, ChevronRight, CheckCircle2,
  XCircle, Clock, RefreshCw, DollarSign,
  AlertTriangle, PieChart, Repeat
} from 'lucide-react';
import { PortfolioTab, AutoInvestTab } from '../components/wallet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  adapter: 'xhr',
});

export default function WalletPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [stripePackages, setStripePackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [allocations, setAllocations] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositing, setDepositing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [tab, setTab] = useState('portfolio');

  // Allocate modal state
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocAgent, setAllocAgent] = useState('');
  const [allocAmount, setAllocAmount] = useState('');
  const [allocating, setAllocating] = useState(false);

  // Withdraw modal state
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAgent, setWithdrawAgent] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const [feedback, setFeedback] = useState(null);

  // Auto-Invest state
  const [aiConfig, setAiConfig] = useState(null);
  const [aiStrategies, setAiStrategies] = useState([]);
  const [aiFrequencies, setAiFrequencies] = useState([]);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiRebalancing, setAiRebalancing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, pkgRes, stripePkgRes, txnRes, allocRes, portfolioRes, aiConfigRes, aiStratRes, aiHistRes] = await Promise.all([
        api.get('/api/wallet/balance'),
        api.get('/api/wallet/packages'),
        api.get('/api/payments/packages'),
        api.get('/api/wallet/transactions'),
        api.get('/api/allocations/me'),
        api.get('/api/wallet/portfolio-summary'),
        api.get('/api/autoinvest/config'),
        api.get('/api/autoinvest/strategies'),
        api.get('/api/autoinvest/history'),
      ]);
      setWallet(walletRes.data);
      setPackages(pkgRes.data.packages);
      setStripePackages(stripePkgRes.data.packages);
      setTransactions(txnRes.data.transactions);
      setAllocations(allocRes.data);
      setPortfolio(portfolioRes.data);
      setAiConfig(aiConfigRes.data);
      setAiStrategies(aiStratRes.data.strategies || []);
      setAiFrequencies(aiStratRes.data.frequencies || []);
      setAiHistory(aiHistRes.data.history || []);
    } catch (err) {
      console.error('Error loading wallet data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll Stripe payment status if returning from checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    setPollingStatus('checking');

    const poll = async () => {
      if (attempts >= 8) {
        setPollingStatus('timeout');
        return;
      }
      try {
        const { data } = await api.get(`/api/payments/status/${sessionId}`);
        if (data.payment_status === 'paid' || data.credited) {
          setPollingStatus('success');
          fetchData();
          return;
        }
        if (data.status === 'expired') {
          setPollingStatus('expired');
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      } catch {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [searchParams, fetchData]);

  const handleSimDeposit = async (pkgId) => {
    setDepositing(pkgId);
    setFeedback(null);
    try {
      const { data } = await api.post('/api/wallet/deposit/simulated', { package_id: pkgId });
      setFeedback({ type: 'success', msg: data.message });
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Deposit failed' });
    } finally {
      setDepositing(null);
    }
  };

  const handleStripeCheckout = async (pkgId) => {
    setPaying(pkgId);
    setFeedback(null);
    try {
      const { data } = await api.post('/api/payments/checkout', {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Payment failed' });
      setPaying(null);
    }
  };

  const handleAllocate = async () => {
    if (!allocAgent || !allocAmount || parseFloat(allocAmount) <= 0) return;
    setAllocating(true);
    setFeedback(null);
    try {
      const { data } = await api.post('/api/wallet/allocate', {
        agent_id: allocAgent,
        amount: parseFloat(allocAmount),
      });
      setFeedback({ type: 'success', msg: data.message });
      setShowAllocate(false);
      setAllocAgent('');
      setAllocAmount('');
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Allocation failed' });
    } finally {
      setAllocating(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAgent || !withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    setWithdrawing(true);
    setFeedback(null);
    try {
      const { data } = await api.post('/api/wallet/withdraw', {
        agent_id: withdrawAgent,
        amount: parseFloat(withdrawAmount),
      });
      setFeedback({ type: 'success', msg: data.message });
      setShowWithdraw(false);
      setWithdrawAgent('');
      setWithdrawAmount('');
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Withdrawal failed' });
    } finally {
      setWithdrawing(false);
    }
  };

  const handleSaveAutoInvest = async () => {
    if (!aiConfig) return;
    setAiSaving(true);
    setFeedback(null);
    try {
      await api.post('/api/autoinvest/config', {
        enabled: aiConfig.enabled,
        strategy: aiConfig.strategy,
        frequency: aiConfig.frequency,
        amount_per_cycle: aiConfig.amount_per_cycle || 0,
        custom_agents: aiConfig.custom_agents || [],
      });
      setFeedback({ type: 'success', msg: aiConfig.enabled ? 'Auto-Invest activated' : 'Auto-Invest deactivated' });
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Error saving config' });
    } finally {
      setAiSaving(false);
    }
  };

  const handleManualRebalance = async () => {
    setAiRebalancing(true);
    setFeedback(null);
    try {
      const { data } = await api.post('/api/autoinvest/rebalance');
      setFeedback({ type: 'success', msg: `${data.message}. Capital: $${data.total_capital?.toLocaleString()}, ${data.moves?.length || 0} movimientos` });
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.detail || 'Rebalance failed' });
    } finally {
      setAiRebalancing(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const agents = [
    'AGT-00','AGT-01','AGT-02','AGT-03','AGT-04','AGT-05','AGT-06','AGT-07',
    'AGT-08','AGT-09','AGT-10','AGT-11','AGT-12','AGT-13','AGT-14','AGT-15','AGT-16'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 font-mono text-sm">{t('wallet.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wallet-page">
      {/* Stripe payment status banner */}
      {pollingStatus && (
        <div className="px-4 md:px-6 pt-4">
          <div className={`flex items-center gap-3 px-4 py-3 border text-sm font-mono ${
            pollingStatus === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
            pollingStatus === 'checking' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' :
            'bg-red-500/10 border-red-500/30 text-red-400'
          }`} data-testid="payment-status-banner">
            {pollingStatus === 'success' && <><CheckCircle2 className="w-5 h-5" /> {t('wallet.paymentSuccess')}</>}
            {pollingStatus === 'checking' && <><RefreshCw className="w-5 h-5 animate-spin" /> {t('wallet.verifyingPayment')}</>}
            {pollingStatus === 'timeout' && <><Clock className="w-5 h-5" /> {t('wallet.paymentTimeout')}</>}
            {pollingStatus === 'expired' && <><XCircle className="w-5 h-5" /> {t('wallet.paymentExpired')}</>}
          </div>
        </div>
      )}

      <main className="p-4 md:p-6 space-y-6">
        {/* Feedback */}
        {feedback && (
          <div className={`flex items-center gap-2 px-4 py-3 border text-sm font-mono ${
            feedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
            'bg-red-500/10 border-red-500/30 text-red-400'
          }`} data-testid="wallet-feedback">
            {feedback.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {feedback.msg}
          </div>
        )}

        {/* Wallet Balance Card */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#090B0F] border border-white/10 p-6" data-testid="wallet-balance-card">
            <div className="flex items-center gap-2 mb-2 text-slate-400">
              <WalletIcon className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('wallet.availableBalance')}</span>
            </div>
            <div className="text-4xl font-black text-emerald-400 tracking-tight" style={{ fontFamily: 'Chivo' }} data-testid="wallet-balance-amount">
              ${wallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-2 text-slate-400">
              <Plus className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('wallet.totalDeposited')}</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="wallet-total-deposited">
              ${wallet?.total_deposited?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-2 text-slate-400">
              <ArrowUpRight className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('wallet.allocatedCapital')}</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="wallet-total-allocated">
              ${wallet?.total_allocated?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-2 text-slate-400">
              <ArrowDownLeft className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('wallet.totalWithdrawn')}</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="wallet-total-withdrawn">
              ${wallet?.total_withdrawn?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
          {[
            { id: 'portfolio', label: 'Portfolio', icon: PieChart },
            { id: 'autoinvest', label: 'Auto-Invest', icon: Repeat },
            { id: 'overview', label: 'Deposit', icon: Plus },
            { id: 'agents', label: 'Assign to Agents', icon: TrendingUp },
            { id: 'stripe', label: 'Real Funds (Stripe)', icon: CreditCard },
            { id: 'history', label: 'History', icon: History },
          ].map(tabItem => (
            <button key={tabItem.id}
              onClick={() => setTab(tabItem.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-wider border-b-2 transition-colors ${
                tab === tabItem.id ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
              data-testid={`wallet-tab-${tabItem.id}`}
            >
              <tabItem.icon className="w-4 h-4" /> {tabItem.label}
            </button>
          ))}
        </div>

        {/* Tab: Portfolio Summary */}
        {tab === 'portfolio' && portfolio && (
          <PortfolioTab portfolio={portfolio} onSwitchTab={setTab} />
        )}

        {/* Tab: Auto-Invest */}
        {tab === 'autoinvest' && aiConfig && (
          <AutoInvestTab
            aiConfig={aiConfig} setAiConfig={setAiConfig}
            aiStrategies={aiStrategies} aiFrequencies={aiFrequencies} agents={agents}
            onSave={handleSaveAutoInvest} onRebalance={handleManualRebalance}
            aiSaving={aiSaving} aiRebalancing={aiRebalancing} aiHistory={aiHistory}
          />
        )}

        {/* Tab: Simulated Deposit */}
        {tab === 'overview' && (
          <div className="space-y-4" data-testid="simulated-deposit-section">
            <h2 className="text-lg font-bold text-white">{t('wallet.depositSimulated')}</h2>
            <p className="text-slate-400 text-sm">{t('wallet.depositSimDesc')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {packages.map(pkg => (
                <button key={pkg.id}
                  onClick={() => handleSimDeposit(pkg.id)}
                  disabled={depositing === pkg.id}
                  className="bg-[#090B0F] border border-white/10 p-6 hover:border-emerald-500/50 transition-all text-left group disabled:opacity-50"
                  data-testid={`sim-deposit-${pkg.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-8 h-8 text-emerald-500 group-hover:scale-110 transition-transform" />
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-500 transition-colors" />
                  </div>
                  <div className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Chivo' }}>{pkg.label}</div>
                  <div className="text-xs font-mono text-slate-500 uppercase">
                    {depositing === pkg.id ? 'PROCESSING...' : `Package ${pkg.id.toUpperCase()}`}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Allocate to Agents */}
        {tab === 'agents' && (
          <div className="space-y-4" data-testid="agent-allocation-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">{t('wallet.assignCapital')}</h2>
              <div className="flex gap-2">
                <button onClick={() => { setShowAllocate(true); setShowWithdraw(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-[#030508] font-bold text-sm hover:bg-emerald-400 transition-colors"
                  data-testid="open-allocate-modal">
                  <ArrowUpRight className="w-4 h-4" /> Assign
                </button>
                <button onClick={() => { setShowWithdraw(true); setShowAllocate(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-bold text-sm hover:bg-white/20 transition-colors"
                  data-testid="open-withdraw-modal">
                  <ArrowDownLeft className="w-4 h-4" /> Withdraw
                </button>
              </div>
            </div>

            {/* Allocate Modal */}
            {showAllocate && (
              <div className="bg-[#090B0F] border border-emerald-500/30 p-6 space-y-4" data-testid="allocate-modal">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-emerald-400">{t('wallet.assignFromWallet')}</h3>
                <p className="text-slate-400 text-xs">{t('wallet.availBalance')} <span className="text-emerald-400 font-bold">${wallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <select value={allocAgent} onChange={e => setAllocAgent(e.target.value)}
                    className="bg-[#11141D] border border-white/10 text-white px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                    data-testid="allocate-agent-select">
                    <option value="">{t('wallet.selectAgent')}</option>
                    {agents.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <input type="number" value={allocAmount} onChange={e => setAllocAmount(e.target.value)}
                    placeholder="Amount ($)" min="0"
                    className="bg-[#11141D] border border-white/10 text-white px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                    data-testid="allocate-amount-input" />
                  <button onClick={handleAllocate} disabled={allocating}
                    className="bg-emerald-500 text-[#030508] font-bold py-3 hover:bg-emerald-400 transition-colors disabled:opacity-50"
                    data-testid="allocate-confirm-btn">
                    {allocating ? t('wallet.assigning') : t('wallet.confirm')}
                  </button>
                </div>
              </div>
            )}

            {/* Withdraw Modal */}
            {showWithdraw && (
              <div className="bg-[#090B0F] border border-amber-500/30 p-6 space-y-4" data-testid="withdraw-modal">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-amber-400">{t('wallet.withdrawToWallet')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <select value={withdrawAgent} onChange={e => setWithdrawAgent(e.target.value)}
                    className="bg-[#11141D] border border-white/10 text-white px-4 py-3 text-sm font-mono focus:outline-none focus:border-amber-500/50"
                    data-testid="withdraw-agent-select">
                    <option value="">{t('wallet.selectAgent')}</option>
                    {allocations?.allocations?.map(a => (
                      <option key={a.agent_id} value={a.agent_id}>{a.agent_id} (${a.amount?.toLocaleString()})</option>
                    ))}
                  </select>
                  <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                    placeholder="Amount ($)" min="0"
                    className="bg-[#11141D] border border-white/10 text-white px-4 py-3 text-sm font-mono focus:outline-none focus:border-amber-500/50"
                    data-testid="withdraw-amount-input" />
                  <button onClick={handleWithdraw} disabled={withdrawing}
                    className="bg-amber-500 text-[#030508] font-bold py-3 hover:bg-amber-400 transition-colors disabled:opacity-50"
                    data-testid="withdraw-confirm-btn">
                    {withdrawing ? 'RETIRANDO...' : 'CONFIRMAR'}
                  </button>
                </div>
              </div>
            )}

            {/* Current Allocations */}
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('wallet.activeAllocations')}</h3>
                <span className="text-xs font-mono text-emerald-400">
                  Total: ${allocations?.summary?.total_invested?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                      <th className="text-left p-3">{t('common.agent')}</th>
                      <th className="text-right p-3">{t('common.invested')}</th>
                      <th className="text-right p-3">{t('wallet.currentValue')}</th>
                      <th className="text-right p-3">P&L</th>
                      <th className="text-right p-3">{t('common.return')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations?.allocations?.length > 0 ? allocations.allocations.map(a => (
                      <tr key={a.agent_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 font-mono text-white">{a.agent_id} <span className="text-slate-500 text-xs">{a.agent_name}</span></td>
                        <td className="p-3 text-right text-white">${a.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="p-3 text-right text-white">${a.current_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className={`p-3 text-right font-mono ${a.simulated_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {a.simulated_pnl >= 0 ? '+' : ''}${a.simulated_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className={`p-3 text-right font-mono ${a.agent_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {a.agent_return_pct >= 0 ? '+' : ''}{a.agent_return_pct?.toFixed(2)}%
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={5} className="p-6 text-center text-slate-500 font-mono text-xs">{t('wallet.noAllocations')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Stripe Real Funds */}
        {tab === 'stripe' && (
          <div className="space-y-4" data-testid="stripe-section">
            <h2 className="text-lg font-bold text-white">{t('wallet.loadRealFunds')}</h2>
            <p className="text-slate-400 text-sm">{t('wallet.loadRealDesc')}</p>
            <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-amber-400 text-xs font-mono flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Modo Test - Los pagos usan tarjetas de prueba de Stripe (4242 4242 4242 4242)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {stripePackages.map(pkg => (
                <button key={pkg.id}
                  onClick={() => handleStripeCheckout(pkg.id)}
                  disabled={paying === pkg.id}
                  className="bg-[#090B0F] border border-white/10 p-6 hover:border-blue-500/50 transition-all text-left group disabled:opacity-50"
                  data-testid={`stripe-pay-${pkg.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <CreditCard className="w-8 h-8 text-blue-400 group-hover:scale-110 transition-transform" />
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400 transition-colors" />
                  </div>
                  <div className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Chivo' }}>{pkg.label}</div>
                  <div className="text-xs font-mono text-slate-500 uppercase">
                    {paying === pkg.id ? 'REDIRIGIENDO...' : 'USD via Stripe'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Transaction History */}
        {tab === 'history' && (
          <div className="space-y-4" data-testid="history-section">
            <h2 className="text-lg font-bold text-white">{t('wallet.transactionHistory')}</h2>
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                      <th className="text-left p-3">{t('common.type')}</th>
                      <th className="text-left p-3">{t('common.description')}</th>
                      <th className="text-right p-3">{t('common.amount')}</th>
                      <th className="text-right p-3">{t('common.balance')}</th>
                      <th className="text-right p-3">{t('common.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? transactions.map((tx, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3">
                          <span className={`px-2 py-0.5 text-xs font-mono ${
                            tx.type === 'simulated_deposit' || tx.type === 'stripe_topup' ? 'bg-emerald-500/20 text-emerald-400' :
                            tx.type === 'allocation' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {tx.type === 'simulated_deposit' ? 'DEPOSITO' :
                             tx.type === 'stripe_topup' ? 'STRIPE' :
                             tx.type === 'allocation' ? 'ASIGNACION' : 'RETIRO'}
                          </span>
                        </td>
                        <td className="p-3 text-white text-xs">{tx.description}</td>
                        <td className={`p-3 text-right font-mono ${tx.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {tx.amount >= 0 ? '+' : ''}${Math.abs(tx.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${tx.balance_after?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="p-3 text-right text-slate-500 text-xs font-mono">{new Date(tx.created_at).toLocaleString()}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={5} className="p-6 text-center text-slate-500 font-mono text-xs">{t('wallet.noTransactions')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
